import concat from 'lodash.concat';
import noop from 'lodash.noop';
import remove from 'lodash.remove';
import QS from 'qs';
import { uuidV4, cancelRequests } from '@adsk/docs-http-client';
import { isBrowser, isNode, isFile, isFilePath } from './common';
import { UPLOAD_STATUS, ERROR, ossCancelTokenMap, DEFAULT_FILE_CHUNK_SIZE, DEFAULT_RETRY_TIMES, AWS_S3_FILE_CHUNK_LIMIT, AWS_S3_UPLOAD_URL_SIGNATURE } from './consts';
export const getSignedUrlPath = ({
  bucketKey,
  objectKey,
  uploadKey,
  firstPart,
  parts,
  minutesExpiration
}) => {
  const query = QS.stringify({
    uploadKey,
    firstPart,
    parts,
    minutesExpiration
  }, {
    addQueryPrefix: true,
    encode: false
  });
  return `/buckets/${bucketKey}/objects/${objectKey}/signeds3upload${query}`;
};
const getIdFromStorageUrn = ossUrn => {
  const relativeUrn = ossUrn.substring(ossUrn.lastIndexOf(':') + 1, ossUrn.length);
  return relativeUrn.split('/');
};
export const getBucketKeyFromObjectId = ossUrn => {
  const idGroup = ossUrn ? getIdFromStorageUrn(ossUrn) : [];
  return idGroup.length > 0 ? idGroup[0] : '';
};
export const getObjectKeyFromObjectId = ossUrn => {
  const idGroup = ossUrn ? getIdFromStorageUrn(ossUrn) : [];
  return idGroup.length > 1 ? idGroup[1] : '';
};

/**
 * Enable with three kinds of `input` type.
 *
 * 1. FileReader, the size is property.
 * 2. Buffer from Node fs.readFile, the size was passed in upload options.
 * 3. FilePath, get size from `fs.statSync`.
 */
export const getFileSize = (input, optionalFileSize) => {
  if (isBrowser()) {
    if (input.size || input.size === 0) return input.size; // File type file
    return input.length; // blob or data URI scheme type file
  } else if (isNode()) {
    if (isFile(input)) {
      if (typeof optionalFileSize !== 'number') throw {
        code: ERROR.FILE_INVALID,
        detail: `filesize parameter is missed when config options for ${input}`
      };
      return optionalFileSize;
    } else if (isFilePath(input)) {
      const fs = require('fs');
      const {
        size
      } = fs.statSync(input);
      return size;
    }
  }
  throw {
    code: ERROR.FILE_INVALID,
    detail: input
  };
};

// get chunk size as multiple of DEFAULT_FILE_CHUNK_SIZE.  48GB: 1X, 50BG: 2X
export const getChunkSize = fileSize => (Math.floor(fileSize / (AWS_S3_FILE_CHUNK_LIMIT * DEFAULT_FILE_CHUNK_SIZE)) + 1) * DEFAULT_FILE_CHUNK_SIZE;

// put 1 empty part to s3 when file is empty
const getFilePartCount = (fileSize, fileChunkSize) => fileSize === 0 ? 1 : Math.ceil(fileSize / fileChunkSize);
const normalizeParts = (partCount, fileId) => {
  const parts = [];
  for (let i = 1; i <= partCount; i++) {
    const part = {
      id: uuidV4(),
      fileId,
      part: i,
      urlExpiration: null,
      url: null,
      getUrlError: null,
      uploadedBytes: 0,
      status: UPLOAD_STATUS.PENDING,
      retries: 0
    };
    parts.push(part);
  }
  return parts;
};

/**
 * 1. abortOnFailure
 * 2. appendHeader('x-ads-acm-namespace', acmNamespace):   ignore
 * 3. appendHeader('x-ads-meta-Cache-Control', 'private; must-revalidate; no-cache')
 * 4. appendHeader('Content-Disposition', `attachment; modification-date="${modifiedTime}"`):  rename to 'x-ads-meta-Content-Disposition'
 * 5. bucket: ignore
 * 6. object: ignore
 * 7. getFiles: ignore
 * 8. getAccessToken: keep it currently
 * 9. sessionID: ignore
 * 10. isAborted
 * 11. isPaused: ignore, never use
 * 12. onProgress
 */
const normalizeFileUploadOptions = (options = {}) => {
  const normalizedOptions = {
    headers: Object.assign({
      'x-ads-meta-Cache-Control': 'private; must-revalidate; no-cache'
    }, options.headers),
    abortOnFailure: options.abortOnFailure || false,
    // oss default value is false
    isAborted: options.isAborted || (() => false),
    // default is false
    onAbort: options.onAbort || noop,
    onProgress: options.onProgress || noop,
    onSuccess: options.onSuccess || noop,
    onFailure: options.onFailure || noop
  };
  return normalizedOptions;
};
export const normalizeFile = ({
  file,
  size,
  objectId,
  fileChunkSize,
  options = {}
}) => {
  const fileId = uuidV4();
  const partCount = getFilePartCount(size, fileChunkSize);
  return {
    id: fileId,
    ref: file,
    fileChunkSize,
    objectId,
    size,
    bucketKey: getBucketKeyFromObjectId(objectId),
    objectKey: getObjectKeyFromObjectId(objectId),
    uploadKey: null,
    partCount,
    uploadExpiration: null,
    status: UPLOAD_STATUS.PENDING,
    progress: 0,
    parts: normalizeParts(partCount, fileId),
    options: normalizeFileUploadOptions(options)
  };
};

// read file as blob in browser
export const readBlob = blob => {
  return new Promise((resolve, reject) => {
    const fileReader = new FileReader();
    fileReader.onloadend = event => {
      event.target.readyState === FileReader.DONE ? resolve(event.target.result) : reject({
        code: ERROR.READ_FILE_ERROR,
        detail: event
      });
    };
    fileReader.onabort = event => reject({
      code: ERROR.READ_FILE_ABORTED,
      detail: event
    });
    fileReader.onerror = event => reject({
      code: ERROR.READ_FILE_ERROR,
      detail: event
    });
    fileReader.readAsArrayBuffer(blob);
  });
};

// read file as buffer in node
export const readFileChunk = async (filePath, part, chunkSize) => {
  const {
    open
  } = require('fs/promises');
  const path = require('path');
  filePath = path.resolve(process.cwd(), filePath);
  let filehandle;
  try {
    filehandle = await open(filePath, 'r');
    const {
      size
    } = await filehandle.stat();
    const offset = (part - 1) * chunkSize;
    const bufferSize = size <= chunkSize ? size : chunkSize + offset <= size ? chunkSize : size - offset;
    const buffer = Buffer.alloc(bufferSize);
    const chunk = await filehandle.read(buffer, 0, bufferSize, offset);
    return chunk.buffer;
  } finally {
    var _filehandle;
    await ((_filehandle = filehandle) === null || _filehandle === void 0 ? void 0 : _filehandle.close());
  }
};
export const getDataURISchemeChunk = (file, partSequence, partSize) => {
  const startAt = (partSequence - 1) * partSize;
  const endBefore = partSequence * partSize >= file.length ? file.length : partSequence * partSize;
  return file.slice(startAt, endBefore);
};
export const getFileBlob = (file, partSequence, partSize) => {
  const startAt = (partSequence - 1) * partSize;
  const endBefore = partSequence * partSize >= file.size ? file.size : partSequence * partSize;
  return file.slice(startAt, endBefore);
};
export const getToUploadFile = queue => queue.find(file => file.status === UPLOAD_STATUS.PENDING);

// file status is UPLOAD_CONTENT_SUCCESS after all parts uploaded to s3 and before make complete to OSS
export const getToCompleteFile = queue => queue.find(file => file.status === UPLOAD_STATUS.UPLOAD_CONTENT_SUCCESS);
const getFileCancelTokenIds = file => {
  const cancelTokenIds = [];
  const ids = concat(file.id, file.parts.map(({
    id
  }) => id));
  ids.forEach(id => {
    const tokenId = ossCancelTokenMap.get(id);
    if (tokenId) {
      cancelTokenIds.push(tokenId);
    }
  });
  return cancelTokenIds;
};
const isAllPartsUploaded = parts => parts.every(part => part.status === UPLOAD_STATUS.UPLOAD_CONTENT_SUCCESS);
const getFileById = (queue, id) => queue.find(file => file.id === id);
const isFileCallbackData = data => data.id && !data.fileId;
const isPartCallbackData = data => data.id && data.fileId;
const updateProgress = (file, data) => {
  let totalUploadedBytes = 0;
  file.parts.forEach(part => {
    if (part.id !== data.id) {
      totalUploadedBytes += part.uploadedBytes || 0;
    } else {
      // update progress for target part
      part.uploadedBytes = data.progress.uploadedBytes;
      totalUploadedBytes += data.progress.uploadedBytes;
    }
  });

  // validate progress and notify
  const progress = Math.floor(totalUploadedBytes / file.size * 100);
  if (file.progress !== progress) {
    file.progress = progress;
    file.options.onProgress(progress);
  }
};

// S3 403 error is WIP: https://autodesk.slack.com/archives/C306ZRD46/p1639165426198400
export const is403ErrorFromS3 = error => {
  var _error$request, _error$response;
  return AWS_S3_UPLOAD_URL_SIGNATURE.test(error === null || error === void 0 ? void 0 : (_error$request = error.request) === null || _error$request === void 0 ? void 0 : _error$request.responseURL) && (error === null || error === void 0 ? void 0 : (_error$response = error.response) === null || _error$response === void 0 ? void 0 : _error$response.status) === 403 ? true : false;
};

// The `queue` is a mutable object which contains all pending upload files
export const updateQueue = (queue, data) => {
  const file = getFileById(queue, data.fileId ? data.fileId : data.id);
  if (!file) return; // may be deleted

  // Check if is progress update first since it will be called frequently
  if (data.progress !== undefined) {
    updateProgress(file, data);
    return;
  }

  // If file upload was cancelled, cancel file's https and remove file from queue
  if (file.options.isAborted()) {
    const tokenIds = getFileCancelTokenIds(file);
    cancelRequests(tokenIds);
    if (file.options.abortOnFailure) {
      file.options.onFailure();
    }
    remove(queue, ({
      id
    }) => id === (data.fileId ? data.fileId : data.id));
    return;
  }
  if (data.error) {
    var _data$error;
    // data.error.config.data is the fileChunk, delete the data.error.config to reduce potential memory leak risk
    if ((_data$error = data.error) !== null && _data$error !== void 0 && _data$error.config) {
      delete data.error.config;
    }

    // For S3 403 error, reset properties, so that the part shall be picked up in the following taskThead
    // After retry DEFAULT_RETRY_TIMES times, call `onFailure`
    if (is403ErrorFromS3(data.error)) {
      const part = file.parts.find(({
        id
      }) => id === data.id);
      if (part.retries <= DEFAULT_RETRY_TIMES) {
        Object.assign(part, {
          retries: part.retries + 1,
          urlExpiration: null,
          url: null,
          progress: null,
          getUrlError: null,
          status: UPLOAD_STATUS.PENDING
        });
        return;
      }
    }

    //fatal error or retried(3 times) failed error, abort upload directly
    file.options.onFailure(data.error);

    // cancel file upload
    const tokenIds = getFileCancelTokenIds(file);
    cancelRequests(tokenIds);
    remove(queue, ({
      id
    }) => id === (data.fileId ? data.fileId : data.id));
    return;
  }
  if (isPartCallbackData(data)) {
    const part = file.parts.find(({
      id
    }) => id === data.id);
    const updatedPart = Object.assign({}, part, data);
    if (data.status === UPLOAD_STATUS.UPLOAD_CONTENT && file.status === UPLOAD_STATUS.PENDING) {
      file.status = UPLOAD_STATUS.UPLOAD_CONTENT;
    }

    // use the first one
    if (!file.uploadExpiration) {
      file.uploadExpiration = updatedPart.uploadExpiration;
    }

    // use the first one
    if (!file.uploadKey && updatedPart.uploadKey) {
      file.uploadKey = updatedPart.uploadKey;
    }

    // should store uploadKey uploadExpiration in file level not part level
    if (updatedPart.uploadKey) {
      delete updatedPart.uploadKey;
    }
    if (updatedPart.uploadExpiration) {
      delete updatedPart.uploadExpiration;
    }
    file.parts.forEach(part => {
      if (updatedPart.id === part.id) {
        Object.assign(part, updatedPart);
      }
    });
    if (isAllPartsUploaded(file.parts) && file.status === UPLOAD_STATUS.UPLOAD_CONTENT) {
      file.status = UPLOAD_STATUS.UPLOAD_CONTENT_SUCCESS;
    }
    return;
  }
  if (isFileCallbackData(data)) {
    // remove upload file from queue and call onSuccess
    if (data.status === UPLOAD_STATUS.COMPLETE_UPLOAD_SUCCESS) {
      file.options.onSuccess(data.res);
      remove(queue, ({
        id
      }) => id === data.id);
      return;
    }
    queue.forEach(file => {
      if (file.id === data.id) {
        Object.assign(file, data);
      }
    });
    return;
  }
};
export const getWaitingForUpload = queue => {
  const pendingFile = getToUploadFile(queue);
  if (pendingFile) {
    const part = pendingFile.parts.find(part => part.status === UPLOAD_STATUS.PENDING);
    /**
     * The other parts must waiting for the first one returns,
     * they relay on the returned uploadKey
     */
    if (!pendingFile.uploadKey && part.part !== 1) {
      return [undefined, undefined];
    }
    return [part, pendingFile];
  } else {
    let part, file;
    const uploadingQueue = queue.filter(file => file.status !== UPLOAD_STATUS.PENDING && file.uploadKey);
    for (let i = 0; i < uploadingQueue.length; i++) {
      part = uploadingQueue[i].parts.find(part => part.status === UPLOAD_STATUS.PENDING);
      if (part) {
        file = queue[i];
        break;
      }
    }
    return [part, file];
  }
};